<!-- 来源：console-google-popup-return-20260915；CST 2026-09-15 17:46；父基线 c79cc29664c6b69410189f64679c7762310e252c。Google 弹窗使用一次性同源 state 桥接；密码登录与邮箱注册流程保持不变。 -->
<script setup lang="ts">
import { onBeforeUnmount, ref, watch } from 'vue';
import {
  loginConsoleFromGuestDialog,
  registerConsoleFromGuestDialog,
  requestConsoleGoogleAuthorization,
  sendConsoleRegistrationCode,
  tokenStorage
} from '../api/client';
import {
  GOOGLE_POPUP_AUTHENTICATED_MESSAGE_TYPE,
  discardGooglePopupState,
  saveGooglePopupState
} from '../auth/googlePopupState';

const props = withDefaults(defineProps<{
  modelValue: boolean;
  reason?: string;
}>(), {
  reason: '登录后即可继续当前操作'
});

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  authenticated: [];
  cancel: [];
}>();

const activeMode = ref<'google' | 'password' | 'register'>('google');
const loading = ref(false);
const googleLoading = ref(false);
const codeLoading = ref(false);
const codeCooldownSeconds = ref(0);
const error = ref('');
const notice = ref('');
const loginEmail = ref('');
const loginPassword = ref('');
const customerName = ref('');
const registerEmail = ref('');
const registerCode = ref('');
const registerPassword = ref('');
// 更新时间：2026-09-04 15:12 CST。确认密码只用于前端防误输，不改变后端既有注册契约。
const registerPasswordConfirmation = ref('');
const registerPasswordVisible = ref(false);
const registerPasswordConfirmationVisible = ref(false);
let googlePopup: Window | null = null;
let googleTimer: number | null = null;
let pendingGoogleState: string | null = null;
let codeCooldownTimer: number | null = null;
let closedAfterAuthentication = false;
let googleMessageListenerActive = false;

watch(() => props.modelValue, visible => {
  if (visible) {
    error.value = '';
    notice.value = '';
    closedAfterAuthentication = false;
  }
});

function finishAuthentication() {
  stopGoogleWatcher(true);
  closedAfterAuthentication = true;
  loading.value = false;
  googleLoading.value = false;
  emit('authenticated');
  emit('update:modelValue', false);
}

async function loginWithPassword() {
  error.value = '';
  notice.value = '';
  if (!loginEmail.value.trim() || !loginPassword.value) {
    error.value = '请输入用户名或注册邮箱以及密码。';
    return;
  }
  loading.value = true;
  try {
    await loginConsoleFromGuestDialog({
      email: loginEmail.value.trim(),
      password: loginPassword.value
    });
    finishAuthentication();
  } catch (cause) {
    error.value = errorMessage(cause, '登录失败，请核对用户名、邮箱和密码。');
  } finally {
    loading.value = false;
  }
}

async function sendRegistrationCode() {
  if (codeLoading.value || codeCooldownSeconds.value > 0) return;
  error.value = '';
  notice.value = '';
  if (!registerEmail.value.trim()) {
    error.value = '请先填写注册邮箱。';
    return;
  }
  codeLoading.value = true;
  try {
    await sendConsoleRegistrationCode(registerEmail.value.trim());
    notice.value = '验证码已经发出';
    startRegistrationCodeCooldown();
  } catch (cause) {
    error.value = errorMessage(cause, '验证码发送失败，请稍后重试。');
  } finally {
    codeLoading.value = false;
  }
}

// 更新时间：2026-09-04 14:41 CST。只有服务端确认接收请求后才开始 60 秒倒计时；请求失败时允许用户立即重试。
function startRegistrationCodeCooldown() {
  stopRegistrationCodeCooldown();
  codeCooldownSeconds.value = 60;
  codeCooldownTimer = window.setInterval(() => {
    if (codeCooldownSeconds.value <= 1) {
      codeCooldownSeconds.value = 0;
      stopRegistrationCodeCooldown();
      return;
    }
    codeCooldownSeconds.value -= 1;
  }, 1000);
}

function stopRegistrationCodeCooldown() {
  if (codeCooldownTimer !== null) {
    window.clearInterval(codeCooldownTimer);
    codeCooldownTimer = null;
  }
}

async function registerWithEmail() {
  error.value = '';
  notice.value = '';
  if (!customerName.value.trim() || !registerEmail.value.trim() || !registerCode.value.trim() || !registerPassword.value || !registerPasswordConfirmation.value) {
    error.value = '请完整填写用户名、邮箱、验证码、密码和确认密码。';
    return;
  }
  if (registerPassword.value !== registerPasswordConfirmation.value) {
    error.value = '两次输入的密码不一致，请重新确认。';
    return;
  }
  loading.value = true;
  try {
    await registerConsoleFromGuestDialog({
      customerName: customerName.value.trim(),
      email: registerEmail.value.trim(),
      verificationCode: registerCode.value.trim(),
      password: registerPassword.value
    });
    finishAuthentication();
  } catch (cause) {
    error.value = errorMessage(cause, '注册失败，请核对填写内容。');
  } finally {
    loading.value = false;
  }
}

async function startGoogleLogin() {
  error.value = '';
  notice.value = '';
  googlePopup = window.open('/login?popup=1', 'hotx-console-google-auth', 'popup=yes,width=520,height=760');
  if (!googlePopup) {
    error.value = '登录窗口被浏览器拦截，请允许本站打开窗口后重试。';
    return;
  }

  googleLoading.value = true;
  try {
    const state = createState();
    pendingGoogleState = state;
    // 更新时间：2026-09-04 17:45 CST。OAuth 回调窗口无法读取父窗口 sessionStorage，使用一次性同源记录完成严格 state 校验。
    saveGooglePopupState(state);
    startGoogleMessageListener();
    const result = await requestConsoleGoogleAuthorization(state);
    const authorizationUrl = new URL(result.authorizationUrl);
    if (authorizationUrl.protocol !== 'https:' || authorizationUrl.hostname !== 'accounts.google.com') {
      throw new Error('登录地址校验失败');
    }
    googlePopup.location.replace(authorizationUrl.toString());
    googleTimer = window.setInterval(() => {
      if (tokenStorage.getAccessToken()) {
        finishAuthentication();
        return;
      }
      if (googlePopup?.closed) {
        stopGoogleWatcher(false);
        error.value = 'Google 登录尚未完成，请重新尝试。';
      }
    }, 500);
  } catch (cause) {
    stopGoogleWatcher(true);
    error.value = errorMessage(cause, '暂时无法连接 Google，请稍后重试。');
  }
}

// 来源：console-google-popup-return-20260915；CST 2026-09-15 17:46；父基线 c79cc29664c6b69410189f64679c7762310e252c。
// 回调页必须携带同源、一次性 state；仅凭页面消息不能完成游客认证，避免第三方页面伪造登录完成信号。
function startGoogleMessageListener() {
  if (googleMessageListenerActive || typeof window === 'undefined') return;
  window.addEventListener('message', handleGooglePopupAuthentication);
  googleMessageListenerActive = true;
}

function handleGooglePopupAuthentication(event: MessageEvent<unknown>) {
  if (event.origin !== window.location.origin || !pendingGoogleState) return;
  const data = event.data;
  if (
    !data
    || typeof data !== 'object'
    || !('type' in data)
    || !('state' in data)
    || data.type !== GOOGLE_POPUP_AUTHENTICATED_MESSAGE_TYPE
    || data.state !== pendingGoogleState
  ) {
    return;
  }
  finishAuthentication();
}

function stopGoogleWatcher(closePopup: boolean) {
  if (googleTimer !== null) {
    window.clearInterval(googleTimer);
    googleTimer = null;
  }
  if (googleMessageListenerActive && typeof window !== 'undefined') {
    window.removeEventListener('message', handleGooglePopupAuthentication);
    googleMessageListenerActive = false;
  }
  if (closePopup && googlePopup && !googlePopup.closed) {
    googlePopup.close();
  }
  discardGooglePopupState(pendingGoogleState);
  pendingGoogleState = null;
  googlePopup = null;
  googleLoading.value = false;
}

function handleClosed() {
  stopGoogleWatcher(true);
  if (!closedAfterAuthentication) {
    emit('cancel');
  }
  closedAfterAuthentication = false;
}

function createState() {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function errorMessage(cause: unknown, fallback: string) {
  return cause instanceof Error && cause.message ? cause.message : fallback;
}

onBeforeUnmount(() => {
  stopGoogleWatcher(true);
  stopRegistrationCodeCooldown();
});
</script>

<template>
  <el-dialog
    :model-value="modelValue"
    width="min(520px, calc(100vw - 32px))"
    class="guest-auth-dialog"
    :close-on-click-modal="false"
    :close-on-press-escape="!loading && !googleLoading"
    destroy-on-close
    append-to-body
    @update:model-value="emit('update:modelValue', $event)"
    @closed="handleClosed"
  >
    <template #header>
      <div class="guest-auth-title">
        <span class="guest-auth-mark">H</span>
        <div>
          <small>HotX CDN</small>
          <h2>登录后继续</h2>
        </div>
      </div>
    </template>

    <div class="guest-auth-reason">
      <strong>{{ reason }}</strong>
      <span>登录完成后弹窗会自动关闭，当前页面和已填写内容都会保留。</span>
    </div>

    <el-alert v-if="error" :title="error" type="error" show-icon :closable="false" />
    <!-- 更新时间：2026-09-04 14:54 CST。以有限次路径动画解释邮箱分类机制，避免非技术用户把垃圾箱投递误认为发送失败。 -->
    <section v-if="notice" class="mail-delivery-guide" role="status" aria-live="polite">
      <span class="mail-delivery-signal" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M4 7.25A2.25 2.25 0 0 1 6.25 5h11.5A2.25 2.25 0 0 1 20 7.25v9.5A2.25 2.25 0 0 1 17.75 19H6.25A2.25 2.25 0 0 1 4 16.75v-9.5Z" />
          <path d="m5 7 7 5.25L19 7" />
        </svg>
      </span>
      <div class="mail-delivery-copy">
        <strong>{{ notice }}</strong>
        <span>暂时没看到？请优先检查“垃圾邮件（垃圾箱）”。</span>
      </div>

      <div class="mail-delivery-route" aria-label="建议依次检查收件箱、垃圾邮件和所有邮件">
        <span class="mail-route-step">收件箱</span>
        <span class="mail-route-link" aria-hidden="true"></span>
        <strong class="mail-route-step mail-route-step--focus">垃圾邮件</strong>
        <span class="mail-route-link" aria-hidden="true"></span>
        <span class="mail-route-step">所有邮件</span>
      </div>

      <p class="mail-delivery-explanation">
        <strong>为什么会在这里？</strong>
        邮箱会根据陌生发件人、自动通知频率等规则进行分类，验证码可能被暂时放进垃圾邮件，并不代表发送失败。
      </p>

      <div class="mail-delivery-search">
        <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <circle cx="8.5" cy="8.5" r="5.5" />
          <path d="m12.5 12.5 4 4" />
        </svg>
        <span>搜索邮件主题：</span>
        <code>CDN Platform 注册验证码</code>
      </div>
    </section>

    <div class="guest-auth-tabs" role="tablist" aria-label="选择登录或注册方式">
      <button type="button" :class="{ active: activeMode === 'google' }" @click="activeMode = 'google'">Google 登录/注册</button>
      <button type="button" :class="{ active: activeMode === 'password' }" @click="activeMode = 'password'">账号登录</button>
      <button type="button" :class="{ active: activeMode === 'register' }" @click="activeMode = 'register'">邮箱注册</button>
    </div>

    <section v-if="activeMode === 'google'" class="guest-auth-panel">
      <button class="google-auth-button" type="button" :disabled="googleLoading" @click="startGoogleLogin">
        <span class="google-letter">G</span>
        {{ googleLoading ? '等待 Google 登录完成...' : '继续使用 Google' }}
      </button>
      <p>首次使用会自动创建客户账号，无需离开当前配置流程。</p>
    </section>

    <form v-else-if="activeMode === 'password'" class="guest-auth-form" @submit.prevent="loginWithPassword">
      <label>用户名或邮箱<input v-model="loginEmail" autocomplete="username" placeholder="请输入用户名或注册邮箱" /></label>
      <label>密码<input v-model="loginPassword" type="password" autocomplete="current-password" placeholder="请输入密码" /></label>
      <button class="guest-auth-primary" type="submit" :disabled="loading">{{ loading ? '登录中...' : '登录并继续' }}</button>
    </form>

    <form v-else class="guest-auth-form" @submit.prevent="registerWithEmail">
      <!-- 后端注册契约继续使用 customerName 字段，仅将客户可见概念统一为“用户名”，保持接口向后兼容。 -->
      <label>用户名<input v-model="customerName" autocomplete="username" placeholder="请输入用户名" /></label>
      <label>邮箱<input v-model="registerEmail" type="email" autocomplete="email" placeholder="请输入邮箱" /></label>
      <label>
        邮箱验证码
        <span class="registration-code-row">
          <input v-model="registerCode" inputmode="numeric" autocomplete="one-time-code" placeholder="请输入验证码" />
          <button
            type="button"
            :disabled="codeLoading || codeCooldownSeconds > 0"
            aria-live="polite"
            @click="sendRegistrationCode"
          >{{ codeLoading ? '发送中...' : codeCooldownSeconds > 0 ? `${codeCooldownSeconds} 秒后可重发` : '发送验证码' }}</button>
        </span>
      </label>
      <!-- 更新时间：2026-09-04 15:12 CST。双密码输入均提供可访问的显示/隐藏按钮，并就地提示不一致。 -->
      <label>
        设置密码
        <span class="password-input-row">
          <input
            v-model="registerPassword"
            :type="registerPasswordVisible ? 'text' : 'password'"
            autocomplete="new-password"
            placeholder="至少 8 位，包含字母和数字"
          />
          <button
            type="button"
            class="password-visibility-toggle"
            :aria-label="registerPasswordVisible ? '隐藏设置密码' : '显示设置密码'"
            :aria-pressed="registerPasswordVisible"
            @click="registerPasswordVisible = !registerPasswordVisible"
          >
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" />
              <circle cx="12" cy="12" r="2.75" />
              <path v-if="registerPasswordVisible" d="m4 4 16 16" />
            </svg>
          </button>
        </span>
      </label>
      <label>
        确认密码
        <span class="password-input-row">
          <input
            v-model="registerPasswordConfirmation"
            :type="registerPasswordConfirmationVisible ? 'text' : 'password'"
            autocomplete="new-password"
            placeholder="请再次输入密码"
          />
          <button
            type="button"
            class="password-visibility-toggle"
            :aria-label="registerPasswordConfirmationVisible ? '隐藏确认密码' : '显示确认密码'"
            :aria-pressed="registerPasswordConfirmationVisible"
            @click="registerPasswordConfirmationVisible = !registerPasswordConfirmationVisible"
          >
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" />
              <circle cx="12" cy="12" r="2.75" />
              <path v-if="registerPasswordConfirmationVisible" d="m4 4 16 16" />
            </svg>
          </button>
        </span>
        <small
          v-if="registerPasswordConfirmation && registerPassword !== registerPasswordConfirmation"
          class="password-match-hint"
          role="alert"
        >两次输入的密码不一致</small>
      </label>
      <button
        class="guest-auth-primary"
        type="submit"
        :disabled="loading || Boolean(registerPasswordConfirmation && registerPassword !== registerPasswordConfirmation)"
      >{{ loading ? '注册中...' : '注册并继续' }}</button>
    </form>
  </el-dialog>
</template>

<style scoped>
.guest-auth-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.guest-auth-title h2,
.guest-auth-title small {
  margin: 0;
}

.guest-auth-title small {
  color: #a66b17;
  font-weight: 800;
}

.guest-auth-mark {
  display: grid;
  width: 42px;
  height: 42px;
  place-items: center;
  border-radius: 13px;
  color: #123e31;
  background: linear-gradient(145deg, #ffe398, #e9b64d);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8), 0 8px 20px rgba(182, 123, 25, 0.2);
  font-size: 20px;
  font-weight: 950;
}

.guest-auth-reason {
  display: grid;
  gap: 5px;
  margin-bottom: 16px;
  padding: 14px 16px;
  border: 1px solid #cfe4da;
  border-radius: 12px;
  color: #264f43;
  background: linear-gradient(120deg, #f2faf6, #fffaf0);
}

.guest-auth-reason span,
.guest-auth-panel p {
  color: #6a7d76;
  font-size: 13px;
}

.mail-delivery-guide {
  position: relative;
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr);
  gap: 10px 12px;
  overflow: hidden;
  padding: 14px;
  border: 1px solid #e5cb8d;
  border-radius: 14px;
  color: #29483e;
  background: linear-gradient(125deg, #f2fbf6 0%, #fff9e9 72%, #fff4d5 100%);
  box-shadow: 0 8px 20px rgba(112, 82, 22, 0.1);
  animation: mail-guide-enter 480ms cubic-bezier(0.16, 1, 0.3, 1) both;
}

.mail-delivery-guide::before {
  position: absolute;
  inset: 0;
  content: '';
  pointer-events: none;
  background: linear-gradient(105deg, transparent 20%, rgba(255, 255, 255, 0.72) 48%, transparent 72%);
  transform: translateX(-110%);
  animation: mail-guide-sheen 850ms 240ms cubic-bezier(0.16, 1, 0.3, 1) 1;
}

.mail-delivery-signal {
  position: relative;
  display: grid;
  width: 42px;
  height: 42px;
  place-items: center;
  border-radius: 13px;
  color: #fff;
  background: #147451;
  box-shadow: 0 7px 16px rgba(20, 116, 81, 0.2);
}

.mail-delivery-signal::after {
  position: absolute;
  inset: -4px;
  border: 2px solid rgba(20, 116, 81, 0.28);
  border-radius: 16px;
  content: '';
  animation: mail-signal-confirm 900ms 3 ease-out;
}

.mail-delivery-signal svg {
  width: 23px;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.8;
}

.mail-delivery-copy {
  display: grid;
  align-content: center;
  gap: 3px;
}

.mail-delivery-copy strong {
  color: #10583f;
  font-size: 15px;
}

.mail-delivery-copy span {
  color: #536d64;
  font-size: 12px;
  line-height: 1.5;
}

.mail-delivery-route {
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: minmax(62px, 1fr) 22px minmax(82px, 1.2fr) 22px minmax(62px, 1fr);
  align-items: center;
  gap: 5px;
}

.mail-route-step {
  display: grid;
  min-height: 30px;
  place-items: center;
  border: 1px solid #c9ddd4;
  border-radius: 9px;
  color: #537068;
  background: rgba(255, 255, 255, 0.72);
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
}

.mail-route-step--focus {
  border-color: #dca640;
  color: #754b05;
  background: #fff1c9;
  box-shadow: 0 5px 13px rgba(186, 124, 13, 0.16);
  animation: mail-spam-focus 1.25s 520ms cubic-bezier(0.16, 1, 0.3, 1) 4;
}

.mail-route-link {
  position: relative;
  height: 1px;
  background: #9fc7b6;
}

.mail-route-link::after {
  position: absolute;
  top: -3px;
  left: 0;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  content: '';
  background: #17815b;
  animation: mail-route-progress 1.25s 420ms ease-in-out 4;
}

.mail-delivery-explanation {
  grid-column: 1 / -1;
  margin: 0;
  color: #5d6f68;
  font-size: 12px;
  line-height: 1.55;
}

.mail-delivery-explanation strong {
  color: #7d570d;
}

.mail-delivery-search {
  display: flex;
  grid-column: 1 / -1;
  align-items: center;
  gap: 5px;
  min-width: 0;
  padding-top: 9px;
  border-top: 1px solid rgba(200, 170, 103, 0.42);
  color: #60736b;
  font-size: 11px;
}

.mail-delivery-search svg {
  flex: 0 0 auto;
  width: 15px;
  stroke: #167353;
  stroke-linecap: round;
  stroke-width: 1.7;
}

.mail-delivery-search code {
  overflow: hidden;
  color: #24594a;
  font-family: inherit;
  font-weight: 850;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@keyframes mail-guide-enter {
  from {
    opacity: 0;
    filter: blur(5px);
    clip-path: inset(0 0 60% 0 round 14px);
    transform: translateY(-6px);
  }
  to {
    opacity: 1;
    filter: blur(0);
    clip-path: inset(0 round 14px);
    transform: translateY(0);
  }
}

@keyframes mail-guide-sheen {
  to { transform: translateX(110%); }
}

@keyframes mail-signal-confirm {
  0% { opacity: 0.7; transform: scale(0.88); }
  75%, 100% { opacity: 0; transform: scale(1.18); }
}

@keyframes mail-spam-focus {
  0%, 100% { box-shadow: 0 5px 13px rgba(186, 124, 13, 0.16); }
  50% { box-shadow: 0 7px 18px rgba(186, 124, 13, 0.32); filter: saturate(1.18); }
}

@keyframes mail-route-progress {
  0% { opacity: 0; transform: translateX(0); }
  20%, 75% { opacity: 1; }
  100% { opacity: 0; transform: translateX(15px); }
}

.guest-auth-tabs {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
  margin: 16px 0;
  padding: 5px;
  border-radius: 12px;
  background: #edf4f0;
}

.guest-auth-tabs button {
  min-height: 38px;
  border: 0;
  border-radius: 9px;
  color: #61736c;
  background: transparent;
  cursor: pointer;
  font-weight: 800;
}

.guest-auth-tabs button.active {
  color: #fff;
  background: linear-gradient(180deg, #176d50, #0c5039);
  box-shadow: 0 6px 15px rgba(16, 91, 65, 0.22);
}

.guest-auth-panel,
.guest-auth-form {
  display: grid;
  gap: 14px;
}

.google-auth-button,
.guest-auth-primary {
  min-height: 48px;
  border: 1px solid #0a573d;
  border-radius: 12px;
  color: #fff;
  background: linear-gradient(180deg, #177552, #0b5038);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.24), 0 10px 22px rgba(9, 79, 55, 0.2);
  cursor: pointer;
  font-size: 15px;
  font-weight: 900;
}

.google-letter {
  display: inline-grid;
  width: 25px;
  height: 25px;
  margin-right: 8px;
  place-items: center;
  border-radius: 50%;
  color: #2878df;
  background: #fff;
  font-weight: 950;
}

.guest-auth-form label {
  display: grid;
  gap: 7px;
  color: #36564c;
  font-size: 13px;
  font-weight: 800;
}

.guest-auth-form input {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  height: 42px;
  border: 1px solid #cfddd7;
  border-radius: 9px;
  padding: 0 12px;
  color: #18372e;
  outline: none;
}

.guest-auth-form input:focus {
  border-color: #258463;
  box-shadow: 0 0 0 3px rgba(37, 132, 99, 0.12);
}

.password-input-row {
  position: relative;
  display: block;
}

.password-input-row input {
  padding-right: 48px;
}

.password-visibility-toggle {
  position: absolute;
  top: 50%;
  right: 6px;
  display: grid;
  width: 34px;
  height: 34px;
  padding: 0;
  place-items: center;
  border: 0;
  border-radius: 7px;
  color: #557068;
  background: transparent;
  cursor: pointer;
  transform: translateY(-50%);
}

.password-visibility-toggle:hover,
.password-visibility-toggle:focus-visible {
  color: #126448;
  background: #edf7f2;
  outline: none;
}

.password-visibility-toggle svg {
  width: 20px;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.7;
}

.password-match-hint {
  color: #c2413a;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.45;
}

.registration-code-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
}

.registration-code-row button {
  border: 1px solid #9fc8b8;
  border-radius: 9px;
  padding: 0 13px;
  color: #176548;
  background: #eff9f4;
  cursor: pointer;
  font-weight: 800;
}

button:disabled {
  cursor: not-allowed;
  opacity: 0.58;
}

@media (max-width: 560px) {
  .guest-auth-tabs {
    grid-template-columns: 1fr;
  }

  .mail-delivery-guide {
    padding: 12px;
  }

  .mail-delivery-route {
    grid-template-columns: minmax(58px, 1fr) 14px minmax(76px, 1.15fr) 14px minmax(58px, 1fr);
    gap: 3px;
  }

  .mail-route-step {
    font-size: 11px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .mail-delivery-guide,
  .mail-delivery-guide::before,
  .mail-delivery-signal::after,
  .mail-route-step--focus,
  .mail-route-link::after {
    animation: none;
  }
}
</style>
